import React from 'react';
import { cn, getInitials } from '@/lib/utils';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  alt?: string;
  name?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * How the initials fallback is drawn. `soft` (the default) is a tint, for
   * light surfaces and dense lists; `solid` is the full brand colour, for the
   * dark chrome, where a tint would disappear.
   */
  variant?: 'soft' | 'solid';
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, src, alt, name, size = 'md', variant = 'soft', ...props }, ref) => {
    const sizes = {
      xs: 'h-6 w-6 text-xs',
      sm: 'h-8 w-8 text-xs',
      md: 'h-10 w-10 text-sm',
      lg: 'h-12 w-12 text-base',
      xl: 'h-16 w-16 text-lg',
    };

    return (
      <div ref={ref} className={cn('relative inline-flex', className)} {...props}>
        {src ? (
          <img
            src={src}
            alt={alt || name || 'Avatar'}
            className={cn(
              'rounded-full object-cover bg-gray-200 dark:bg-gray-700',
              sizes[size]
            )}
          />
        ) : (
          <div
            className={cn(
              'rounded-full flex items-center justify-center font-semibold',
              variant === 'solid'
                ? 'bg-primary-500 text-white'
                : 'bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 ring-1 ring-inset ring-primary-600/10 dark:ring-primary-300/10',
              sizes[size]
            )}
          >
            {getInitials(name)}
          </div>
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

export { Avatar };
